/* eslint-disable max-len */
import React from 'react';

import { css } from '@emotion/css';
import { useTranslation } from 'react-i18next';

import { COLORS2_AS_FOREGROUND, font2 } from '^/constants/commonStyles';
import { CURRENCY_CODE_KEYS } from '^/constants/currencyCode.constants';
import { RawReservationDetailExtraChange } from '^/types/ReservationDetailed/RawReservationDetailed';
import { formatKRWWonAmount } from '^/util/formatKRWWonAmount';

import { flexSpacedRowWithVerticalCenter } from './desktop/components/ReservationDetailDesktop.commonStuff';
import { ExtraChargeDisplayCurrency } from './extraChargeDisplay';

const defaultStyles = {
  titleText: [font2.Headline1_Regular, COLORS2_AS_FOREGROUND.main.MrtBlack].join(' '),

  totalText: [font2.Headline1_Bold, COLORS2_AS_FOREGROUND.main.MrtBlack].join(' '),

  itemTitleText: [font2.Caption1_Light, COLORS2_AS_FOREGROUND.sub.MrtGray1].join(' '),

  itemTotalText: [font2.Caption1_Light, COLORS2_AS_FOREGROUND.sub.MrtGray1].join(' '),

  row: [
    css`
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      align-items: center;
    `,
  ].join(' '),
} as const;

export const AdditionalFees2: React.FC<{
  extra_charge: RawReservationDetailExtraChange;
  onSiteCurrency: CURRENCY_CODE_KEYS;
  displayCurrency?: ExtraChargeDisplayCurrency;
  stylesOverride?: Partial<typeof defaultStyles>;
}> = ({ extra_charge, onSiteCurrency, displayCurrency = 'foreign', stylesOverride }) => {
  const isKrwDisplay = displayCurrency === 'krw';
  const currencyLabel = isKrwDisplay ? '원' : onSiteCurrency;
  const totalAmount = isKrwDisplay
    ? extra_charge.additional_rate_amount
    : extra_charge.additional_amount;
  const getItemAmount = (item: RawReservationDetailExtraChange['extra_charge_detail'][number]) => {
    if (!isKrwDisplay) return item.price;
    return item.price_krw ?? item.price;
  };
  const getItemTotal = (item: RawReservationDetailExtraChange['extra_charge_detail'][number]) => {
    const itemAmount = Number(getItemAmount(item)) || 0;
    const itemQuantity = Number(item.quantity) || 0;
    return itemAmount;
  };
  const formatAmount = (value?: number | string | null) => {
    if (isKrwDisplay) return formatKRWWonAmount(value);
    if (value === null || value === undefined) return null;
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) {
      return value?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    }
    const rawValue = numericValue.toFixed(2);
    return rawValue.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };
  const styles = {
    ...defaultStyles,
    ...stylesOverride,
  };
  return (
    <div className="flex flex-col gap-2">
      {!isKrwDisplay && (
        <div className={flexSpacedRowWithVerticalCenter}>
          <p className={[styles.titleText].join(' ')}>추가 요금</p>
          <p className={[styles.totalText].join(' ')}>
          {formatAmount(totalAmount)} {currencyLabel}
          </p>
        </div>
      )}
      {extra_charge.extra_charge_detail.map((item) => {
        return (
          <div key={item.name} className={styles.row}>
            <p className={[styles.itemTitleText].join(' ')}>{`${item.name} (${item.quantity})`}</p>
            <p className={[styles.itemTotalText].join(' ')}>
              {formatAmount(getItemTotal(item))} {currencyLabel}
            </p>
          </div>
        );
      })}
      {extra_charge.dropFee > 0 && (
        <div className={styles.row}>
          <p className={styles.itemTitleText}>Drop fee</p>
          <p className={styles.itemTotalText}>
            {formatAmount(extra_charge.dropFee)} {currencyLabel}
          </p>
        </div>
      )}
      {extra_charge.etc_amount > 0 && (
        <div className={styles.row}>
          <p className={styles.itemTitleText}>기타 (세금 등)</p>
          <p className={styles.itemTotalText}>
            {formatAmount(extra_charge.etc_amount)} {currencyLabel}
          </p>
        </div>
      )}
    </div>
  );
};
